import React, { useState }from 'react';
import {
  Tabs,
  Tab,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import InvoiceCancel from './InvoiceCancel';
import CloseIcon from '@mui/icons-material/Close';
import { useReverseStatementOnlineMutation } from '../../services/statementOnline';
import {
  useDeleteStatementMutation,
  useGetCaseLogQuery,
  useRestoreStatementMutation,
} from '../../services/matchStatement';
import SortableTableLabel from '../../components/Table/SortableTableLabel';
// import { set } from 'lodash';

const caseLogColumns = [
  { id: 'createdAt', label: 'Created At', minWidth: 150 },
  { id: 'case_type', label: 'Case Type', minWidth: 140 },
  { id: 'statement_ref', label: 'Statement Ref', minWidth: 180 },
  { id: 'transaction_id', label: 'Transaction ID', minWidth: 160 },
  { id: 'result', label: 'Result', minWidth: 220 },
  { id: 'executed_by', label: 'Executed By', minWidth: 140 },
];

const formatDateTime = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const caseActionConfig = {
  reverse: {
    title: 'Confirm Reverse Statement',
    actionLabel: 'Reverse',
    color: 'primary',
    toneClass: 'border-green-500 bg-green-50 text-green-800',
    buttonClass: 'border-green-500 bg-green-50 text-green-800',
    description: 'คืน Statement Online ที่เคย match/partial ให้กลับเป็น Unmatch',
    impact: 'ใช้เมื่อรายการถูก match ผิด และต้องการให้กลับไปตรวจสอบหรือ match ใหม่',
  },
  delete: {
    title: 'Confirm Delete Statement',
    actionLabel: 'Delete Statement',
    color: 'error',
    toneClass: 'border-red-500 bg-red-50 text-red-800',
    buttonClass: 'border-red-500 bg-red-50 text-red-800',
    description: 'ซ่อน Statement แบบ soft delete จากหน้า Statement Online > Statement, Matching และ Report และยัง Restore กลับได้',
    impact: 'ใช้เมื่อ Statement ไม่ควรถูกแสดงหรือใช้งานในระบบ แต่ยังต้องเก็บประวัติไว้ตรวจสอบ',
  },
  restore: {
    title: 'Confirm Restore Statement',
    actionLabel: 'Restore Statement',
    color: 'warning',
    toneClass: 'border-orange-500 bg-orange-50 text-orange-800',
    buttonClass: 'border-orange-500 bg-orange-50 text-orange-800',
    description: 'กู้คืน Statement ที่เคยถูก Delete Statement ไว้ให้กลับมาใช้งาน',
    impact: 'ใช้เมื่อพบว่ารายการที่ถูกลบไว้ควรกลับมาแสดงและนำไป match/report ได้อีกครั้ง',
  },
};

const caseActionOrder = ['reverse', 'delete', 'restore'];

const extractStatementRefs = (input) => {
  const value = String(input || '').trim();
  if (!value) return [];

  const separatedRefs = value
    .split(/[\s,;|]+/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (separatedRefs.length) return separatedRefs;

  return value.match(/[A-Za-z]{2,6}\d{8,}/g) || [];
};

const formatStatementRefs = (input) => extractStatementRefs(input).join(', ');

function Case() {
// Component state หรอ
  const [statementRefInput, setStatementRefInput] = useState('');
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [logPage, setLogPage] = useState(0);
  const [logRowsPerPage, setLogRowsPerPage] = useState(5);
  const [logSearch, setLogSearch] = useState('');
  const [logSortBy, setLogSortBy] = useState('createdAt');
  const [logSortOrder, setLogSortOrder] = useState('desc');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [activeAction, setActiveAction] = useState('delete');
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmRefs, setConfirmRefs] = useState([]);
  const [reverseStatementOnline, { isLoading }] = useReverseStatementOnlineMutation();
  const [deleteStatement, { isLoading: isDeletingStatement }] = useDeleteStatementMutation();
  const [restoreStatement, { isLoading: isRestoringStatement }] = useRestoreStatementMutation();
  const {
    data: caseLogData,
    isLoading: isCaseLogLoading,
    isFetching: isCaseLogFetching,
    refetch: refetchCaseLog,
  } = useGetCaseLogQuery({
    page: logPage + 1,
    limit: logRowsPerPage,
    search: logSearch,
    sortBy: logSortBy,
    sortOrder: logSortOrder,
  });

  const handleLogSort = (property) => {
    setLogSortOrder(
      logSortBy === property && logSortOrder === 'asc' ? 'desc' : 'asc',
    );
    setLogSortBy(property);
    setLogPage(0);
  };

  const parseStatementRef = extractStatementRefs;

  const isCaseActionLoading = isLoading || isDeletingStatement || isRestoringStatement;

  const getResultSeverity = (response) => {
    const failCount = Number(response?.failCount || 0);
    const successCount = Number(response?.successCount || 0);
    const totalCount = Number(response?.totalCount || 0);

    if (successCount === 0 && (failCount > 0 || totalCount === 0)) return 'error';
    if (failCount > 0) return 'warning';
    return 'success';
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: false,
      message,
      severity,
    });

    setTimeout(() => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    }, 0);
  };

  const getInputRefsOrWarn = () => {
    const statementRefs = parseStatementRef(statementRefInput);

    if (!statementRefs.length) {
      const message = 'กรุณาระบุ Statement Ref อย่างน้อย 1 รายการ';
      setErrorMessage(message);
      setResult(null);
      showSnackbar(message, 'error');
      return [];
    }

    return statementRefs;
  };

  const openConfirmDialog = (action) => {
    const statementRefs = getInputRefsOrWarn();
    if (!statementRefs.length) return;

    setConfirmAction(action);
    setConfirmRefs(statementRefs);
  };

  const closeConfirmDialog = () => {
    if (isCaseActionLoading) return;
    setConfirmAction(null);
    setConfirmRefs([]);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;

    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleStatementRefPaste = (event) => {
    const pastedText = event.clipboardData?.getData('text') || '';
    const refs = extractStatementRefs(pastedText);

    if (refs.length <= 1) return;

    event.preventDefault();

    setStatementRefInput((currentValue) => {
      const currentRefs = extractStatementRefs(currentValue);
      return [...currentRefs, ...refs].join(', ');
    });
  };

  const handleStatementRefBlur = () => {
    const formattedValue = formatStatementRefs(statementRefInput);
    if (formattedValue && formattedValue !== statementRefInput) {
      setStatementRefInput(formattedValue);
    }
  };

  const runReverseStatement = async (statementRefs) => {
    try {
      setErrorMessage('');
      const response = await reverseStatementOnline({
        statementRef: statementRefs,
      }).unwrap();
      setResult({ ...response, caseAction: 'Reverse' });
      showSnackbar(response.message || 'Reverse completed', getResultSeverity(response));
      setLogPage(0);
      refetchCaseLog();
    } catch (error) {
      const message = error?.data?.message
        || error?.data?.error?.message
        || error?.message
        || 'Reverse failed';
      setResult(null);
      setErrorMessage(message);
      showSnackbar(message, 'error');
    }
  };

  const runDeleteStatement = async (statementRefs) => {
    try {
      setErrorMessage('');
      const response = await deleteStatement({
        statementRef: statementRefs,
      }).unwrap();
      setResult({ ...response, caseAction: 'Delete Statement' });
      showSnackbar(response.message || 'Delete completed', getResultSeverity(response));
      setLogPage(0);
      refetchCaseLog();
    } catch (error) {
      const message = error?.data?.message
        || error?.data?.error?.message
        || error?.message
        || 'Delete failed';
      setResult(null);
      setErrorMessage(message);
      showSnackbar(message, 'error');
    }
  };

  const runRestoreStatement = async (statementRefs) => {
    try {
      setErrorMessage('');
      const response = await restoreStatement({
        statementRef: statementRefs,
      }).unwrap();
      setResult({ ...response, caseAction: 'Restore Statement' });
      showSnackbar(response.message || 'Restore completed', getResultSeverity(response));
      setLogPage(0);
      refetchCaseLog();
    } catch (error) {
      const message = error?.data?.message
        || error?.data?.error?.message
        || error?.message
        || 'Restore failed';
      setResult(null);
      setErrorMessage(message);
      showSnackbar(message, 'error');
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    openConfirmDialog(activeAction);
  };

  const handleConfirmAction = async () => {
    const action = confirmAction;
    const refs = confirmRefs;

    setConfirmAction(null);
    setConfirmRefs([]);

    if (action === 'reverse') {
      await runReverseStatement(refs);
    } else if (action === 'delete') {
      await runDeleteStatement(refs);
    } else if (action === 'restore') {
      await runRestoreStatement(refs);
    }
  };

  const handleClear = () => {
    setStatementRefInput('');
    setResult(null);
    setErrorMessage('');
  };

  const handleLogPageChange = (_, newPage) => {
    setLogPage(newPage);
  };

  const handleLogRowsPerPageChange = (event) => {
    setLogRowsPerPage(parseInt(event.target.value, 10));
    setLogPage(0);
  };

  const handleLogSearchChange = (event) => {
    setLogSearch(event.target.value);
    setLogPage(0);
  };

  const caseLogs = caseLogData?.data || [];
  const caseLogTotal = caseLogData?.pagination?.total || 0;
  const resultSeverity = getResultSeverity(result);
  const activeConfirmConfig = confirmAction ? caseActionConfig[confirmAction] : null;
  const activeActionConfig = caseActionConfig[activeAction];
  const parsedStatementRefs = parseStatementRef(statementRefInput);
  const previewStatementRefs = parsedStatementRefs.slice(0, 10);
  const hiddenPreviewCount = Math.max(parsedStatementRefs.length - previewStatementRefs.length, 0);

  return (
    <div className="py-6">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-lg font-bold">Case Management</div>
          <div className="mt-1 text-sm text-gray-600">
            Enter one or more statement refs. Use comma or new line.
          </div>
        </div>
        <div className="text-sm text-gray-500">
          Ready: <span className="font-bold text-gray-800">{parsedStatementRefs.length}</span> refs
        </div>
      </div>
      <Paper className="mt-4 p-4">
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Statement refs"
                placeholder="STM001, STM002"
                value={statementRefInput}
                onChange={(event) => setStatementRefInput(event.target.value)}
                onPaste={handleStatementRefPaste}
                onBlur={handleStatementRefBlur}
                error={Boolean(errorMessage)}
                helperText={errorMessage || 'Paste from Excel/linesได้ ระบบจะเติม comma ให้อัตโนมัติ'}
              />
            </div>

            <div className="lg:col-span-5">
              <div className="text-sm font-bold text-gray-700">Action</div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
                {caseActionOrder.map((action) => {
                  const config = caseActionConfig[action];
                  const isSelected = activeAction === action;

                  return (
                    <button
                      key={action}
                      type="button"
                      onClick={() => setActiveAction(action)}
                      className={[
                        'rounded border p-3 text-left text-sm transition',
                        isSelected
                          ? `${config.buttonClass} shadow-sm`
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400',
                      ].join(' ')}
                    >
                      <div className="font-bold">{config.actionLabel}</div>
                      <div className="mt-1 leading-5">{config.description}</div>
                    </button>
                  );
                })}
              </div>

              <div className={`mt-3 rounded border p-3 text-sm ${activeActionConfig.toneClass}`}>
                <div className="font-bold">Selected: {activeActionConfig.actionLabel}</div>
                <div className="mt-1 leading-5">{activeActionConfig.impact}</div>
              </div>

              <div className="mt-3 rounded border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-bold text-gray-700">Detected refs</div>
                  <Chip label={`${parsedStatementRefs.length} refs`} size="small" />
                </div>
                <div className="mt-2 flex max-h-28 flex-wrap gap-1 overflow-auto">
                  {previewStatementRefs.length ? (
                    previewStatementRefs.map((ref) => (
                      <Chip key={ref} label={ref} size="small" />
                    ))
                  ) : (
                    <div className="text-sm text-gray-500">No refs detected</div>
                  )}
                  {hiddenPreviewCount ? (
                    <Chip label={`+${hiddenPreviewCount} more`} size="small" variant="outlined" />
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
            <div className="text-sm text-gray-500">
              {parsedStatementRefs.length ? `${parsedStatementRefs.length} refs ready` : 'No refs ready'}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outlined" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="submit"
                variant="contained"
                color={activeActionConfig.color}
                disabled={isCaseActionLoading}
                sx={{ color: '#fff' }}
              >
                {isCaseActionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  activeActionConfig.actionLabel
                )}
              </Button>
            </div>
          </div>
        </form>
      </Paper>

      {errorMessage ? (
        <Alert severity="error" className="mt-4">
          {errorMessage}
        </Alert>
      ) : null}

      {result ? (
        <Paper className="mt-4 p-4">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-sm text-gray-500">Latest result</div>
              <div className="text-base font-bold text-gray-800">
                {result.caseAction || 'Case Action'}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Chip label={`Success ${result.successCount}`} size="small" color="success" />
              <Chip
                label={`Failed ${result.failCount}`}
                size="small"
                color={Number(result.failCount || 0) ? 'error' : 'default'}
              />
              <Chip label={`Total ${result.totalCount}`} size="small" />
            </div>
          </div>
          <Alert severity={resultSeverity} className="mb-3">
            {result.message || 'Done'}
          </Alert>
          {Array.isArray(result.details) && result.details.length ? (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <div className="mb-2 font-bold">รายละเอียด</div>
              <div className="flex flex-col gap-1">
                {result.details.map((detail) => (
                  <div key={detail} className="flex items-start gap-2">
                    <Chip label="เหตุผล" size="small" color={resultSeverity} />
                    <span>{detail}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Paper>
      ) : null}

      <Paper className="mt-4 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-base font-bold">Case Log</div>
          <TextField
            size="small"
            label="Search log"
            value={logSearch}
            onChange={handleLogSearchChange}
            sx={{ minWidth: 260 }}
          />
        </div>

        <TableContainer className="mt-4" sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {caseLogColumns.map((column) => (
                  <TableCell key={column.id} sx={{ minWidth: column.minWidth, fontWeight: 700 }}>
                    <SortableTableLabel
                      column={column}
                      sortBy={logSortBy}
                      sortOrder={logSortOrder}
                      onSort={handleLogSort}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isCaseLogLoading || isCaseLogFetching ? (
                <TableRow>
                  <TableCell colSpan={caseLogColumns.length} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : caseLogs.length ? (
                caseLogs.map((row) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>{row.case_type || '-'}</TableCell>
                    <TableCell>{row.statement_ref || '-'}</TableCell>
                    <TableCell>{row.transaction_id || '-'}</TableCell>
                    <TableCell>{row.result || '-'}</TableCell>
                    <TableCell>{row.executed_by || '-'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={caseLogColumns.length} align="center">
                    No logs
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={caseLogTotal}
          rowsPerPage={logRowsPerPage}
          page={logPage}
          onPageChange={handleLogPageChange}
          onRowsPerPageChange={handleLogRowsPerPageChange}
        />
      </Paper>


      <Snackbar
        key={`${snackbar.severity}-${snackbar.message}`}
        open={snackbar.open}
        autoHideDuration={12000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          transform: 'translateY(-50%)',
        }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{
            width: '100%',
            minWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 620 },
            alignItems: 'center',
          }}
          variant="filled"
          action={(
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleSnackbarClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(activeConfirmConfig)}
        onClose={closeConfirmDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{activeConfirmConfig?.title}</DialogTitle>
        <DialogContent>
          <Alert severity={confirmAction === 'delete' ? 'warning' : 'info'} className="mb-3">
            {activeConfirmConfig?.description}
          </Alert>
          <div className="text-sm text-gray-700">
            <div className="font-bold">ผลกระทบ</div>
            <div className="mt-1">{activeConfirmConfig?.impact}</div>
            <div className="mt-4 font-bold">Statement Ref ที่จะดำเนินการ ({confirmRefs.length})</div>
            <div className="mt-2 max-h-40 overflow-auto rounded border border-gray-200 bg-gray-50 p-2">
              {confirmRefs.map((ref) => (
                <Chip key={ref} label={ref} size="small" className="m-1" />
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={isCaseActionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={activeConfirmConfig?.color}
            onClick={handleConfirmAction}
            disabled={isCaseActionLoading}
            sx={{ color: '#fff' }}
          >
            {activeConfirmConfig?.actionLabel}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}

function CaseManagement() {
  const [tab, setTab] = useState('statement');
  return (
    <div className="min-w-0 w-full pt-4 pb-6">
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 48, '& .MuiTab-root': { textTransform: 'none', minHeight: 48 } }} aria-label="Case Management">
        <Tab value="statement" label="Statement" />
        <Tab value="cancel" label="ลบรายการ Cancel" />
      </Tabs>
      {tab === 'statement' ? <Case /> : <InvoiceCancel />}
    </div>
  );
}

export default CaseManagement;
